/* eslint-disable react/no-unknown-property */
import {
  Action, HStack, ICON_ACCOUNT_CHECK_OUTLINE, ICON_ACCOUNT_OUTLINE, ICON_CARD_TEXT_OUTLINE, ICON_EMAIL_OUTLINE, ICON_HOME_OUTLINE, ICON_PHONE_OUTLINE, Icon, DetailItemList,
} from '@doveit/bricks';
import React, { FunctionComponent, useCallback } from 'react';
import Card, { CardStatus } from '../../../components/card/Card';
import { Contact } from '../../../providers/api/dtos/contact';
import * as styles from './ContactMatchCard.style';

export interface ContactMatchCardProps {
  contact: Contact,
  newContactValues?: Partial<Contact>,
  onSelected: (existingContact: Contact, newContactValues?: Partial<Contact>) => void,
  onUpdate?: (existingContact: Contact, newContactValues?: Partial<Contact>) => void,
}

const ContactMatchCard: FunctionComponent<ContactMatchCardProps> = ({
  contact,
  onSelected,
  onUpdate,
  newContactValues,
}) => {
  const onInternalSelected = useCallback(
    () => onSelected(contact),
    [contact, onSelected],
  );

  const onInternalUpdate = useCallback(
    () => {
      if (onUpdate) {
        onUpdate(contact, newContactValues);
      }
    },
    [contact, newContactValues, onUpdate],
  );

  return (
    <Card status={contact.complete ? CardStatus.SUCCESS : undefined}>
      <Card.Inline>
        <Card.Box>
          <DetailItemList>
            {(contact.name || newContactValues?.name) && (
              <DetailItemList.Item icon={ICON_ACCOUNT_OUTLINE}>
                <span aria-label="Indicazione del nome">
                  {contact.name}
                </span>
                {newContactValues?.name && newContactValues.name !== contact.name && (
                  <>
                    <span>⤑</span>
                    <span
                      css={styles.searchValue}
                      aria-label="Indicazione del nuovo nome"
                    >
                      {newContactValues.name}
                    </span>
                  </>
                )}
              </DetailItemList.Item>
            )}
            {(contact.phoneNumber || newContactValues?.phoneNumber) && (
              <DetailItemList.Item icon={ICON_PHONE_OUTLINE}>
                <span aria-label="Indicazione del numero di telefono">
                  {contact.phoneNumber}
                </span>
                {newContactValues?.phoneNumber && newContactValues.phoneNumber !== contact.phoneNumber && (
                  <>
                    <span>⤑</span>
                    <span
                      css={styles.searchValue}
                      aria-label="Indicazione del nuovo numero di telefono"
                    >
                      {newContactValues.phoneNumber}
                    </span>
                  </>
                )}
              </DetailItemList.Item>
            )}
            {(contact.email || newContactValues?.email) && (
              <DetailItemList.Item icon={ICON_EMAIL_OUTLINE}>
                <span aria-label="Indicazione dell'indirizzo email">
                  {contact.email}
                </span>
                {newContactValues?.email && newContactValues.email !== contact.email && (
                  <>
                    <span>⤑</span>
                    <span
                      css={styles.searchValue}
                      aria-label="Indicazione del nuovo indirizzo email"
                    >
                      {newContactValues.email}
                    </span>
                  </>
                )}
              </DetailItemList.Item>
            )}
            {contact.fiscalCode && (
              <DetailItemList.Item icon={ICON_CARD_TEXT_OUTLINE}>
                {contact.fiscalCode}
              </DetailItemList.Item>
            )}
            {contact.residence?.normalizedAddress && (
              <DetailItemList.Item icon={ICON_HOME_OUTLINE}>
                {contact.residence?.normalizedAddress}
              </DetailItemList.Item>
            )}
          </DetailItemList>
        </Card.Box>
        <Card.Box fit>
          <div
            css={styles.rightBox}
            $complete={contact.complete}
          >
            {contact.complete && (
              <div css={styles.completeContact}>
                Completo
                {' '}
                <Icon
                  path={ICON_ACCOUNT_CHECK_OUTLINE}
                />
              </div>
            )}
            <HStack>
              <Action
                aria-label="Pulsante per selezionare il contatto"
                label="Seleziona"
                size="S"
                emphasis="high"
                color="info"
                onClick={onInternalSelected}
              />
              {onUpdate && (
                <Action
                  aria-label="Pulsante per aggiornare il contatto"
                  dataRef="update-button"
                  label="Aggiorna"
                  size="S"
                  emphasis="high"
                  color="critical"
                  onClick={onInternalUpdate}
                />
              )}
            </HStack>
          </div>
        </Card.Box>
      </Card.Inline>
    </Card>
  );
};

export default ContactMatchCard;
